import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { useTheme } from '@/context/ThemeContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Globe, 
  Sun, 
  Moon, 
  Calculator, 
  FileSpreadsheet, 
  FileText, 
  History, 
  List, 
  Sparkles,
  Menu,
  X,
  BookOpen
} from 'lucide-react';

const Header = ({ activeTab, setActiveTab }) => {
  const { language, setLanguage, t, LANGUAGE_OPTIONS, isAutoDetected } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Handle language change with URL navigation
  const handleLanguageChange = (newLang) => {
    // Get current path
    const currentPath = location.pathname;
    
    // Remove current language prefix if exists
    const supportedLangs = ['en', 'de', 'fr', 'it', 'es'];
    let basePath = currentPath;
    
    for (const lang of supportedLangs) {
      if (currentPath === `/${lang}` || currentPath.startsWith(`/${lang}/`)) {
        basePath = currentPath.slice(lang.length + 1) || '/';
        break;
      }
    }
    
    // Ensure basePath starts with /
    if (!basePath.startsWith('/')) {
      basePath = '/' + basePath;
    }
    
    // Build new URL with new language
    let newPath;
    if (newLang === 'en') {
      // English uses root path
      newPath = basePath === '/' ? '/' : basePath;
    } else {
      // Other languages use prefix
      newPath = basePath === '/' ? `/${newLang}` : `/${newLang}${basePath}`;
    }
    
    // Update language state
    setLanguage(newLang);
    
    // Navigate to new URL
    navigate(newPath);
    
    // Announce to screen readers
    announceToScreenReader(`${t('languageChanged')} ${LANGUAGE_OPTIONS.find(l => l.code === newLang)?.name}`);
  };

  const tabs = [
    { id: 'calculator', icon: Calculator, label: t('calculator'), description: t('appDescription') },
    { id: 'bulk', icon: FileSpreadsheet, label: t('bulkCalculator'), description: t('bulkDescription') },
    { id: 'invoice', icon: FileText, label: t('invoiceCalculator'), description: t('invoiceDescription') },
    { id: 'rates', icon: List, label: t('vatRates'), description: t('allRatesFor') + ' 62 ' + t('euCountries') },
    { id: 'history', icon: History, label: t('history'), description: t('calculationHistory') },
  ];

  // Blog URL based on language
  const getBlogUrl = () => {
    if (language === 'en') return '/blog';
    return `/${language}/blog`;
  };

  // Get home URL based on current language
  const getHomeUrl = () => {
    if (language === 'en') return '/';
    return `/${language}`;
  };

  // Handle tab change with announcement
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setMobileMenuOpen(false);
    // Announce tab change to screen readers
    const tab = tabs.find(t => t.id === tabId);
    if (tab) {
      announceToScreenReader(`${tab.label} tab selected`);
    }
  };

  // Simple announcement helper
  const announceToScreenReader = (message) => {
    const announcement = document.getElementById('sr-announcements');
    if (announcement) {
      announcement.textContent = '';
      setTimeout(() => {
        announcement.textContent = message;
      }, 100);
    }
  };

  // Check if on privacy page
  const isPrivacyPage = location.pathname.includes('/privacy');

  return (
    <header 
      className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60" 
      data-testid="header"
      role="banner"
    >
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Mobile Menu Button - Only visible on mobile/tablet */}
          {!isPrivacyPage && (
            <div className="lg:hidden">
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    aria-label="Menu"
                  >
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[300px] sm:w-[350px]">
                  <SheetHeader className="text-left">
                    <SheetTitle className="flex items-center gap-2">
                      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-emerald-500">
                      <Calculator className="w-5 h-5 text-white" />
                    </div>
                    <span className="bg-gradient-to-r from-blue-600 to-emerald-600 bg-clip-text text-transparent">
                      {t('appName')}
                    </span>
                  </SheetTitle>
                </SheetHeader>
                
                <Separator className="my-4" />
                
                {/* Mobile Navigation */}
                <nav className="flex flex-col gap-2" role="navigation" aria-label="Main navigation">
                  {tabs.map((tab) => (
                    <SheetClose asChild key={tab.id}>
                      <button
                        onClick={() => handleTabChange(tab.id)}
                        className={`
                          flex items-start gap-4 p-4 rounded-lg text-left transition-colors
                          ${activeTab === tab.id 
                            ? 'bg-primary text-primary-foreground' 
                            : 'hover:bg-accent'
                          }
                        `}
                        aria-current={activeTab === tab.id ? 'page' : undefined}
                      >
                        <div className={`
                          p-2 rounded-lg 
                          ${activeTab === tab.id 
                            ? 'bg-primary-foreground/20' 
                            : 'bg-muted'
                          }
                        `}>
                          <tab.icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">{tab.label}</p>
                          <p className={`text-sm truncate ${
                            activeTab === tab.id 
                              ? 'text-primary-foreground/70' 
                              : 'text-muted-foreground'
                          }`}>
                            {tab.description}
                          </p>
                        </div>
                      </button>
                    </SheetClose>
                  ))}
                </nav>

                <Separator className="my-4" />

                {/* Language Selection in Mobile Menu */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground px-2">{t('language')}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {LANGUAGE_OPTIONS.map((lang) => (
                      <SheetClose asChild key={lang.code}>
                        <button
                          onClick={() => handleLanguageChange(lang.code)}
                          className={`
                            flex items-center gap-2 p-3 rounded-lg text-sm transition-colors
                            ${language === lang.code 
                              ? 'bg-primary text-primary-foreground' 
                              : 'bg-muted hover:bg-accent'
                            }
                          `}
                          aria-checked={language === lang.code}
                        >
                          <span>{lang.flag}</span>
                          <span>{lang.name}</span>
                        </button>
                      </SheetClose>
                    ))}
                  </div>
                </div>

                <Separator className="my-4" />

                {/* Theme Toggle in Mobile Menu */}
                <div className="flex items-center justify-between px-2">
                  <span className="text-sm font-medium text-muted-foreground">{t('theme')}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      toggleTheme();
                      const newTheme = theme === 'light' ? 'dark' : 'light';
                      announceToScreenReader(`${t('themeChanged')} ${newTheme} mode`);
                    }}
                    className="gap-2"
                  >
                    {theme === 'light' ? (
                      <>
                        <Moon className="h-4 w-4" />
                        {t('darkMode')}
                      </>
                    ) : (
                      <>
                        <Sun className="h-4 w-4" />
                        {t('lightMode')}
                      </>
                    )}
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
            </div>
          )}

          {/* Logo - CLICKABLE to home */}
          <Link 
            to={getHomeUrl()}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-lg"
            data-testid="logo"
            aria-label={`${t('appName')} - ${t('appTagline')}. Click to go to homepage.`}
            onClick={() => {
              if (!isPrivacyPage) {
                setActiveTab('calculator');
              }
            }}
          >
            <div 
              className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-emerald-500"
              aria-hidden="true"
            >
              <Calculator className="w-6 h-6 text-white" aria-hidden="true" />
            </div>
            <div>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-emerald-600 bg-clip-text text-transparent">
                {t('appName')}
              </span>
              <p className="text-xs text-muted-foreground hidden sm:block" aria-hidden="true">
                {t('appTagline')}
              </p>
            </div>
          </Link>

          {/* Navigation - Desktop */}
          {!isPrivacyPage && (
            <nav 
              id="main-navigation"
              className="hidden lg:flex items-center gap-1" 
              data-testid="nav-desktop"
              role="navigation"
              aria-label="Main navigation"
              tabIndex={-1}
            >
              {tabs.map((tab) => (
                <Button
                  key={tab.id}
                  variant={activeTab === tab.id ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => handleTabChange(tab.id)}
                  className="gap-2"
                  data-testid={`nav-${tab.id}`}
                  aria-current={activeTab === tab.id ? 'page' : undefined}
                  aria-label={`${tab.label}${activeTab === tab.id ? ' (current page)' : ''}`}
                >
                  <tab.icon className="w-4 h-4" aria-hidden="true" />
                  {tab.label}
                </Button>
              ))}
            </nav>
          )}

          {/* Actions - Desktop */}
          <div className="flex items-center gap-1" role="toolbar" aria-label="Settings">
            {/* Language Selector - Desktop only */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="gap-2 hidden sm:flex"
                  data-testid="language-selector"
                  aria-label={`${t('language')}: ${LANGUAGE_OPTIONS.find(l => l.code === language)?.name || 'English'}`}
                  aria-haspopup="menu"
                >
                  <Globe className="h-4 w-4" aria-hidden="true" />
                  <span>{LANGUAGE_OPTIONS.find(l => l.code === language)?.flag}</span>
                  {isAutoDetected && (
                    <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">
                      <Sparkles className="w-3 h-3 mr-0.5" aria-hidden="true" />
                      Auto
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" role="menu" aria-label="Language selection">
                {LANGUAGE_OPTIONS.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className={language === lang.code ? 'bg-accent' : ''}
                    data-testid={`lang-${lang.code}`}
                    role="menuitemradio"
                    aria-checked={language === lang.code}
                  >
                    <span className="mr-2" aria-hidden="true">{lang.flag}</span>
                    {lang.name}
                    {language === lang.code && isAutoDetected && (
                      <Badge variant="outline" className="ml-2 text-[10px]">Auto</Badge>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Theme Toggle - Desktop only */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => {
                toggleTheme();
                const newTheme = theme === 'light' ? 'dark' : 'light';
                announceToScreenReader(`${t('themeChanged')} ${newTheme} mode`);
              }} 
              data-testid="theme-toggle"
              className="hidden sm:flex"
              aria-label={`${t('theme')}: ${theme === 'light' ? t('lightMode') : t('darkMode')}. Click to switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
              aria-pressed={theme === 'dark'}
            >
              {theme === 'light' ? (
                <Moon className="h-5 w-5" aria-hidden="true" />
              ) : (
                <Sun className="h-5 w-5" aria-hidden="true" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
